/**
 * Setup guide (tracker item 75, phase 2), ADMIN only: a linear stepper over
 * the same ten steps as the dashboard checklist. It resumes at the first
 * step that is neither done nor skipped.
 *
 * The wizard writes no data itself: each step's page is the existing settings
 * page, opened with "Open full settings page" (which shows a way back here).
 * Only the progress state changes through this page (skip, mark done, hide).
 * "Save and continue" on a data-backed step re-reads the checklist and moves
 * on only when the step's data now exists; the manual steps have "Mark done".
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, ISetupApi } from '../api';
import * as types from '../api/types';
import { useAuth } from '../context/AuthContext';
import { canManageSetup } from '../utils/permissions';
import { getErrorMessage } from '../utils/apiError';
import { STARTER_PACKS_PATH } from '../utils/starterUi';
import { FROM_SETUP_STATE, SETUP_STEP_HELP, firstUnresolvedIndex, stepStateLabel } from '../utils/setupWizard';
import { primaryButtonClass, secondaryButtonClass } from '../components/formStyles';
import { Toast } from '../components/Toast';

const tapButton = 'min-h-[44px]';
const linkButton =
  'inline-flex items-center min-h-[44px] text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline';

export function SetupWizardPage() {
  const { user } = useAuth();
  const allowed = canManageSetup(user);
  const api = useMemo<ISetupApi>(() => createAPIClient(), []);
  const [progress, setProgress] = useState<types.SetupProgress | null>(null);
  // Position in the step list; steps.length is the finished panel. Null until the first load picks the resume point.
  const [index, setIndex] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoadError(null);
    return api
      .getSetup()
      .then((result) => {
        setProgress(result);
        setIndex((current) => current ?? firstUnresolvedIndex(result.steps));
      })
      .catch((err) => setLoadError(getErrorMessage(err, 'The setup guide could not be loaded.')));
  }, [api]);

  // Only an ADMIN may read the checklist: nobody else makes the request
  useEffect(() => {
    if (allowed) void load();
  }, [allowed, load]);

  /** Run a progress change; resolves true when it worked. */
  const change = useCallback(async (action: () => Promise<types.SetupProgress>, failure: string): Promise<boolean> => {
    setBusy(true);
    setError(null);
    setHint(null);
    try {
      setProgress(await action());
      return true;
    } catch (err) {
      setError(getErrorMessage(err, failure));
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  if (!allowed) return null;

  if (loadError && !progress) {
    return (
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Setup guide</h1>
        <Toast kind="error">{loadError}</Toast>
        <button type="button" className={secondaryButtonClass} onClick={() => void load()}>
          Try again
        </button>
      </main>
    );
  }
  if (!progress || index === null) {
    return (
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Setup guide</h1>
        <p role="status" className="text-gray-600">Loading your setup progress...</p>
      </main>
    );
  }

  const steps = progress.steps;
  const finished = index >= steps.length;
  const step = finished ? null : steps[index];
  const advance = () => {
    setHint(null);
    setIndex((i) => Math.min((i ?? 0) + 1, steps.length));
  };
  const back = () => {
    setHint(null);
    setIndex((i) => Math.max((i ?? 0) - 1, 0));
  };

  const saveAndContinue = async (current: types.SetupStep) => {
    setBusy(true);
    setError(null);
    setHint(null);
    try {
      const latest = await api.getSetup();
      setProgress(latest);
      if (latest.steps.find((s) => s.key === current.key)?.done) {
        advance();
      } else {
        setHint('This step is not done yet. Open the full settings page, finish it there, then come back and press Save and continue. You can also skip it.');
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Your progress could not be checked.'));
    } finally {
      setBusy(false);
    }
  };

  const skip = async (current: types.SetupStep) => {
    if (await change(() => api.skipSetupStep(current.key), 'The step could not be skipped.')) advance();
  };
  const markDone = async (current: types.SetupStep) => {
    if (await change(() => api.markSetupStepDone(current.key), 'The step could not be marked done.')) advance();
  };

  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      {error && <Toast kind="error">{error}</Toast>}
      <h1 className="text-2xl font-bold text-gray-900">Setup guide</h1>
      <p className="mt-1 text-sm text-gray-600">
        {progress.resolvedCount} of {progress.total} steps done or skipped
      </p>
      <div
        role="progressbar"
        aria-label="Setup progress"
        aria-valuemin={0}
        aria-valuemax={progress.total}
        aria-valuenow={progress.resolvedCount}
        aria-valuetext={`${progress.resolvedCount} of ${progress.total} steps done or skipped`}
        className="mt-2 h-2 w-full rounded-full bg-gray-200 overflow-hidden"
      >
        <div className="h-full bg-blue-600" style={{ width: `${progress.total ? (progress.resolvedCount / progress.total) * 100 : 0}%` }} />
      </div>

      {progress.dismissed && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
          The checklist is hidden on your dashboard.{' '}
          <button
            type="button"
            disabled={busy}
            className={`${linkButton} disabled:opacity-50`}
            onClick={() => void change(() => api.setSetupDismissed(false), 'The setup checklist could not be shown.')}
          >
            Show checklist
          </button>
        </div>
      )}

      <ol aria-label="Setup steps" className="mt-4 divide-y divide-gray-100 rounded-lg bg-white shadow">
        {steps.map((s, i) => (
          <li
            key={s.key}
            data-step={s.key}
            data-state={stepStateLabel(s).toLowerCase().replace(' ', '-')}
            aria-current={i === index ? 'step' : undefined}
            className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 ${i === index ? 'bg-blue-50' : ''}`}
          >
            <span className="w-6 shrink-0 text-sm font-semibold text-gray-500" aria-hidden="true">{i + 1}</span>
            <span className={`min-w-0 flex-1 break-words ${i === index ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
              {s.title}
              {s.optional && <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">Optional</span>}
            </span>
            <span className={`text-xs font-semibold uppercase tracking-wide ${s.done ? 'text-green-700' : s.skipped ? 'text-gray-500' : 'text-amber-700'}`}>
              {stepStateLabel(s)}
            </span>
          </li>
        ))}
      </ol>

      <section className="mt-6 rounded-lg bg-white p-4 sm:p-6 shadow" aria-labelledby="setup-step-heading">
        {step ? (
          <>
            <p className="text-sm text-gray-500">Step {index + 1} of {steps.length}</p>
            <h2 id="setup-step-heading" className="mt-1 text-xl font-bold text-gray-900">
              {step.title}
              {step.optional && <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">Optional</span>}
            </h2>
            <p className="mt-2 text-gray-700">{SETUP_STEP_HELP[step.key]}</p>
            <p className="mt-2 text-sm font-semibold text-gray-700">Status: {stepStateLabel(step)}</p>
            <div className="mt-3">
              <Link to={step.link} state={FROM_SETUP_STATE} className={linkButton}>
                Open full settings page
              </Link>
              {step.key === 'items' && !step.done && (
                <Link to={STARTER_PACKS_PATH} state={FROM_SETUP_STATE} className={`${linkButton} ml-4`}>
                  Start from an industry pack
                </Link>
              )}
            </div>
            {hint && <p role="status" className="mt-2 rounded-md bg-amber-50 p-3 text-sm text-amber-800">{hint}</p>}

            <div className="mt-4 flex flex-wrap items-center gap-3">
              {step.done ? (
                <>
                  <button type="button" disabled={busy} className={`${primaryButtonClass} ${tapButton}`} onClick={advance}>
                    Continue
                  </button>
                  {step.manual && (
                    <button
                      type="button"
                      disabled={busy}
                      className={`${secondaryButtonClass} ${tapButton}`}
                      onClick={() => void change(() => api.unmarkSetupStepDone(step.key), 'The step could not be changed.')}
                    >
                      Undo mark done
                    </button>
                  )}
                </>
              ) : step.skipped ? (
                <>
                  <button type="button" disabled={busy} className={`${primaryButtonClass} ${tapButton}`} onClick={advance}>
                    Continue
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    className={`${secondaryButtonClass} ${tapButton}`}
                    onClick={() => void change(() => api.unskipSetupStep(step.key), 'The step could not be unskipped.')}
                  >
                    Unskip
                  </button>
                </>
              ) : (
                <>
                  {step.manual ? (
                    <button type="button" disabled={busy} className={`${primaryButtonClass} ${tapButton}`} onClick={() => void markDone(step)}>
                      Mark done
                    </button>
                  ) : (
                    <button type="button" disabled={busy} className={`${primaryButtonClass} ${tapButton}`} onClick={() => void saveAndContinue(step)}>
                      Save and continue
                    </button>
                  )}
                  <button type="button" disabled={busy} className={`${secondaryButtonClass} ${tapButton}`} onClick={() => void skip(step)}>
                    Skip
                  </button>
                </>
              )}
              <button type="button" disabled={busy || index === 0} className={`${secondaryButtonClass} ${tapButton}`} onClick={back}>
                Back
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 id="setup-step-heading" className="text-xl font-bold text-gray-900">
              {progress.complete ? 'Setup is complete' : 'Some steps are still open'}
            </h2>
            <p className="mt-2 text-gray-700">
              {progress.complete
                ? 'Every required step is done or skipped. You can reopen this guide any time from the dashboard, or from Settings if you hide the checklist.'
                : 'Go back to finish or skip the steps marked To do.'}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Link to="/" className={`${primaryButtonClass} ${tapButton} inline-flex items-center`}>
                Go to the dashboard
              </Link>
              {!progress.dismissed && (
                <button
                  type="button"
                  disabled={busy}
                  className={`${secondaryButtonClass} ${tapButton}`}
                  onClick={() => void change(() => api.setSetupDismissed(true), 'The setup checklist could not be hidden.')}
                >
                  Hide checklist
                </button>
              )}
              <button type="button" disabled={busy} className={`${secondaryButtonClass} ${tapButton}`} onClick={back}>
                Back
              </button>
            </div>
          </>
        )}
      </section>
    </main>
  );
}
